import type { ResourceArticle } from '~/types/ResourceArticle'

export const understandingDepressionArticle: ResourceArticle = {
  uid: 'understanding-depression',
  thumbnailImageUrl: 'https://cdn.hubofhope.co.uk/public/img/thumbs/RESOURCES_DEPRESSION_800x800.jpg',
  thumbnailImageAlt: 'Depression',
  title: 'Understanding depression',
  supportNeedCategoryUid: 'A37O2XH8iwZIIWOZo2vE',
  supportNeedCategoryLabel: 'Depression',
  isFeatured: true,
  excerpt:
    'All of us go through periods where we feel unhappy and fed up, but sometimes experiences of low mood, sadness, tearfulness, difficulty sleeping, lack of energy, and loss of interest in the things we enjoy – often referred to as depression – can persist for weeks or months.',
  content: `
  <div class="prose mt-4">
    <p class="font-bold">
      All of us go through periods where we feel unhappy and fed up, but sometimes experiences of low mood, sadness, tearfulness, difficulty sleeping, lack of energy, and loss of interest in the things we enjoy – often referred to as depression – can persist for weeks or months.
    </p>
  
    <h2 class="font-semibold text-lg font-brown text-primary mt-6">Sometimes it’s hard to work out what started feelings of depression</h2>
    <p>
      Although a common experience, it can be very distressing – and even life-threatening for those of us who feel so desperate that we consider taking our own lives. Often it is related to the events and circumstances of our lives and the meaning that they have for us. This is particularly true for loss, or experiences that make us feel hopeless, humiliated, defeated, ashamed, or trapped 
      (<a href="" class="text-blue-500 underline hover:text-blue-700">see Trauma & post-traumatic stress</a>). Sometimes it’s hard to work out what started it, but we feel stuck in a vicious circle of despair and lethargy.
    </p>
  
    <figure class="my-8">
      <img src="https://cdn.hubofhope.co.uk/public/img/thumbs/youssef-naddam-iJ2IG8ckCpA-unsplash_800x800.jpg"
           alt="Understanding depression article image" 
           class="max-w-md w-full h-auto rounded shadow-md mx-auto">
    </figure>
  
    <p>
      Some of us experience periods of depression and also periods where we feel elated, 'high' and full of energy, but also often agitated, restless, and we find it hard to sleep. This experience is sometimes called 'bipolar' 
      (<a href="/resource/understanding-bipolar" class="text-blue-500 underline hover:text-blue-700">see Understanding bipolar experiences</a>).
    </p>
  
    <h2 class="font-semibold text-lg font-brown text-primary mt-6">How to seek help</h2>
    <p>
      In terms of help, different things help different people. Often practical things such as self-care are central, including exercise, eating, and sleeping well.
    </p>
  
    <p class="italic my-2">
      “Doing things that connect us with others, including people with similar experiences, either informally or in organised peer support groups can be a big help.”
    </p>
  
    <p>
      Therapy or counselling can help us understand what might have led to our feelings of depression and what might be keeping it going, and can help us find ways to support ourselves and to regulate our physical and emotional states. Some people find medication helpful. Depression and loneliness often go hand in hand, so finding ways to connect or reconnect with friends, families, and communities can be key.
    </p>
  
    <p class="mb-8 mt-4">
      For further information see this British Psychological Society booklet called 
      <a href="/resource/understanding-depression" class="text-blue-500 underline hover:text-blue-700">Understanding Depression</a>.
    </p>
  </div>`
}
